import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronLeft, CheckCircle2, IndianRupee } from "lucide-react";
import PatientAppLayout from "../../components/PatientAppLayout/PatientAppLayout";
import { apiRequest, getApiBaseUrl } from "../../Api/Api";
import { ENDPOINTS } from "../../Api/Endpoints";
import { getSocket } from "../../Api/socket";
import "./PatientChatsHub.scss";
import "./PatientChatShared.scss";
import {
  PATIENT_CHAT_TIMELINE_TYPES,
  PatientChatAdminNoticeCard,
  PatientChatSystemCard,
  procedureLabelFromBidHistory,
} from "./PatientChatShared";
import PatientChatsHubBidBookingPanel from "./PatientChatsHubBidBookingPanel";
import {
  isPatientChatThreadUnread,
  markPatientChatThreadSeen,
} from "../../utils/patientChatReadState";
import {
  parsePatientChatsInboxPreview,
  splitInrPreviewAmountDisplay,
} from "../../utils/patientChatsInboxPreview";
import {
  clearActiveChatConversationId,
  setActiveChatConversationId,
} from "../../utils/activeChatConversation";
import { usePatientShellChrome } from "../../Layout/patientShellChrome";
import ChatDeliveryTicks, { getOutgoingReceiptStatus } from "../../components/ChatDeliveryTicks/ChatDeliveryTicks";
import { SkeletonBlock } from "../../components/Skeleton/Skeleton";

function formatWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
}

function outgoingReceiptLabel(status, peerLabel = "doctor") {
  if (status === "read") return `Seen by ${String(peerLabel || "doctor").toLowerCase()}`;
  if (status === "delivered") return `Delivered to ${String(peerLabel || "doctor").toLowerCase()}`;
  return "Sent";
}

/** WhatsApp-style “last seen” from an ISO string (matches BidHistoryChat). */
function formatProviderLastSeen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "";
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = d.toDateString() === yesterday.toDateString();
  const time = d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  if (sameDay) return `Last seen today at ${time}`;
  if (isYesterday) return `Last seen yesterday at ${time}`;
  return `Last seen ${d.toLocaleDateString("en-IN", { day: "numeric", month: "short" })} at ${time}`;
}

function getConversationTitle(c) {
  const name =
    c?.providerName ||
    c?.targetName ||
    c?.target?.name ||
    c?.doctorName ||
    c?.hospitalName ||
    "";
  if (name && String(name).trim()) return String(name).trim();
  return c?.targetRole === "hospital" ? "Hospital" : "Care provider";
}

function getInitials(name) {
  const s = String(name || "").trim();
  if (!s) return "?";
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase().slice(0, 2);
  }
  return s.slice(0, 2).toUpperCase();
}

function asImageUrlString(v) {
  if (v == null || v === "") return "";
  if (typeof v === "string") return v.trim();
  if (typeof v === "object" && typeof v.url === "string") return v.url.trim();
  const s = String(v).trim();
  return s && s !== "[object Object]" ? s : "";
}

/** Google Places photo URLs often 403 with `referrerPolicy="no-referrer"`; omit for those hosts. */
/** If API returns `/uploads/...`, load from API origin (not the React dev server). */
function absolutePatientMediaUrl(src) {
  const s = String(src || "").trim();
  if (!s) return "";
  if (/^https?:\/\//i.test(s)) return s;
  if (s.startsWith("//")) return `https:${s}`;
  if (s.startsWith("/")) {
    try {
      return `${String(getApiBaseUrl()).replace(/\/$/, "")}${s}`;
    } catch {
      return s;
    }
  }
  return s;
}

function bidLedgerRowWrapClass(message) {
  if (String(message?.messageType || "") !== "system") return "";
  const t = String(message?.payload?.type || "");
  if (t === "reverse_bid" || t === "provider_accept" || t === "provider_reject") {
    return "patient-chats-hub__sys-wrap--ledger-provider";
  }
  if (t === "initial_bid" || t === "patient_counter" || t === "patient_accept" || t === "patient_reject") {
    return "patient-chats-hub__sys-wrap--ledger-patient";
  }
  return "patient-chats-hub__sys-wrap--ledger-neutral";
}

/** Hide provider-targeted admin notes from the patient app (API may still include them in edge cases). */
function patientSeesAdminTextMessage(m) {
  if (String(m?.senderRole || "").toLowerCase() !== "admin" || String(m?.messageType || "") !== "text") return true;
  const aud = String(m?.adminAudience || "both").toLowerCase();
  return aud !== "provider";
}

function avatarImgReferrerPolicy(src) {
  if (!src) return undefined;
  try {
    const u = new URL(src, typeof window !== "undefined" ? window.location.href : "http://localhost");
    const h = u.hostname.toLowerCase();
    if (
      h.includes("googleusercontent") ||
      h.includes("ggpht.com") ||
      h.includes("maps.googleapis.com") ||
      h === "lh3.googleusercontent.com"
    ) {
      return undefined;
    }
  } catch (_) {
    return undefined;
  }
  return "no-referrer";
}

/** Inbox row: provider portrait (patient view) — field names differ by API / proxy layers. */
function pickProviderImageFromListItem(c) {
  if (!c || typeof c !== "object") return "";
  const candidates = [
    c.providerImage,
    c.provider_image,
    c.targetImage,
    c.target_image,
    c.target?.image,
    c.target?.avatar,
    c.target?.profileImage,
    c.doctorImage,
    c.doctor_image,
    c.doctor?.image,
    c.doctor?.avatar,
    c.peerImage,
    c.peer_image,
    c.provider?.image,
    c.provider?.avatar,
  ];
  for (const x of candidates) {
    const s = asImageUrlString(x);
    if (s) return s;
  }
  return "";
}

/** Normalize provider portrait from GET …/messages / conversation payloads (shape varies by API version). */
function pickProviderImageFromMessagesPayload(res) {
  if (!res || typeof res !== "object") return "";
  const candidates = [
    res.providerImage,
    res.provider_image,
    res.conversation?.providerImage,
    res.conversation?.provider_image,
    res.target?.image,
    res.target?.avatar,
    res.provider?.image,
    res.provider?.avatar,
    res.doctorImage,
    res.doctor?.image,
  ];
  for (const x of candidates) {
    const s = asImageUrlString(x);
    if (s) return s;
  }
  return "";
}

function PatientChatsHubBoardSkeleton() {
  return (
    <div
      className="patient-chats-hub__board patient-chats-hub__board--skeleton"
      aria-busy="true"
      aria-label="Loading conversations"
    >
      <span className="patient-chats-hub__sr-only">Loading conversations</span>
      <div className="patient-chats-hub__panel patient-chats-hub__panel--list">
        <ul className="patient-chats-hub__list">
          {Array.from({ length: 7 }).map((_, i) => (
            <li key={`sk-inbox-${i}`}>
              <div className="patient-chats-hub__sk-inbox-row">
                <SkeletonBlock width={44} height={44} className="patient-chats-hub__sk-avatar" />
                <div className="patient-chats-hub__sk-inbox-body">
                  <div className="patient-chats-hub__sk-inbox-top">
                    <SkeletonBlock
                      height={15}
                      width={i % 3 === 0 ? "min(200px, 68%)" : i % 3 === 1 ? "min(160px, 55%)" : "min(140px, 48%)"}
                      className="patient-chats-hub__sk-line"
                    />
                    <SkeletonBlock height={11} width={76} className="patient-chats-hub__sk-line patient-chats-hub__sk-line--time" />
                  </div>
                  <div className="patient-chats-hub__sk-inbox-mid">
                    <SkeletonBlock height={18} width={54} className="patient-chats-hub__sk-badge" />
                  </div>
                  <SkeletonBlock
                    height={13}
                    width={i % 2 === 0 ? "min(100%, 90%)" : "min(100%, 72%)"}
                    className="patient-chats-hub__sk-line patient-chats-hub__sk-line--preview"
                  />
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
      <div className="patient-chats-hub__panel patient-chats-hub__panel--preview">
        <div className="patient-chats-hub__preview-head patient-chats-hub__preview-head--skeleton">
          <div className="patient-chats-hub__preview-head-text">
            <SkeletonBlock width={40} height={40} className="patient-chats-hub__sk-avatar patient-chats-hub__sk-avatar--sm" />
            <div className="patient-chats-hub__sk-head-text">
              <SkeletonBlock height={17} width="min(220px, 55%)" className="patient-chats-hub__sk-line" />
              <SkeletonBlock height={13} width="min(180px, 48%)" className="patient-chats-hub__sk-line patient-chats-hub__sk-line--sub" />
            </div>
          </div>
        </div>
        <div className="patient-chats-hub__preview-stream patient-chats-hub__preview-stream--skeleton">
          <PatientChatsHubStreamSkeleton showBottomStrip />
        </div>
      </div>
    </div>
  );
}

function PatientChatsHubStreamSkeleton({ showBottomStrip = false }) {
  const rows = [
    { side: "left", h: 76, tw: "min(100%, 88%)" },
    { side: "right", h: 52, tw: "min(100%, 72%)" },
    { side: "right", h: 84, tw: "min(100%, 80%)" },
    { side: "left", h: 64, tw: "min(100%, 90%)" },
    { side: "right", h: 58, tw: "min(100%, 68%)" },
  ];
  return (
    <div className="patient-chats-hub__sk-stream-root">
      <div className="patient-chats-hub__sk-procedure">
        <SkeletonBlock width={36} height={36} className="patient-chats-hub__sk-proc-icon" />
        <div className="patient-chats-hub__sk-proc-copy">
          <SkeletonBlock height={11} width={72} className="patient-chats-hub__sk-line" />
          <SkeletonBlock height={14} width="94%" className="patient-chats-hub__sk-line patient-chats-hub__sk-line--mt" />
          <SkeletonBlock height={14} width="76%" className="patient-chats-hub__sk-line patient-chats-hub__sk-line--mt2" />
        </div>
      </div>
      <div className="patient-chats-hub__sk-chat-rows" aria-hidden>
        {rows.map((row, i) => (
          <div
            key={`sk-chat-${i}`}
            className={`patient-chats-hub__sk-chat-row patient-chats-hub__sk-chat-row--${row.side}`.trim()}
          >
            <SkeletonBlock height={row.h} width={row.tw} className="patient-chats-hub__sk-chat-card hb_skeleton" />
          </div>
        ))}
      </div>
      {showBottomStrip ? (
        <div className="patient-chats-hub__sk-booking">
          <div className="patient-chats-hub__sk-booking-main">
            <SkeletonBlock height={13} width="42%" className="patient-chats-hub__sk-line" />
            <SkeletonBlock height={12} width="88%" className="patient-chats-hub__sk-line patient-chats-hub__sk-line--mt" />
            <SkeletonBlock height={12} width="70%" className="patient-chats-hub__sk-line patient-chats-hub__sk-line--mt2" />
            <SkeletonBlock height={12} width="56%" className="patient-chats-hub__sk-line patient-chats-hub__sk-line--mt2" />
          </div>
          <div className="patient-chats-hub__sk-booking-actions">
            <SkeletonBlock width={40} height={38} className="patient-chats-hub__sk-pill" />
            <SkeletonBlock width={40} height={38} className="patient-chats-hub__sk-pill" />
          </div>
        </div>
      ) : null}
    </div>
  );
}

function PatientChatsHubInboxAmountPreview({ amount, caption, expired = false }) {
  const amtParts = splitInrPreviewAmountDisplay(amount);
  return (
    <span
      className={`patient-chats-hub__row-preview-stack patient-chats-hub__row-preview-stack--price${
        expired ? " patient-chats-hub__row-preview-stack--expired" : ""
      }`.trim()}
      aria-label={`${caption}. ${amount}`}
    >
      <span className="patient-chats-hub__row-preview-price-shell">
        <span className="patient-chats-hub__row-preview-price-accent" aria-hidden />
        <span className="patient-chats-hub__row-preview-price-body">
          <span className="patient-chats-hub__row-preview-price-kicker">
            <span className="patient-chats-hub__row-preview-price-kicker-icon" aria-hidden>
              <IndianRupee size={11} strokeWidth={2.75} />
            </span>
            Price quote
          </span>
          <span className="patient-chats-hub__row-preview-amount">
            {amtParts.currency && amtParts.figure ? (
              <>
                <span className="patient-chats-hub__row-preview-currency">{amtParts.currency}</span>
                <span className="patient-chats-hub__row-preview-figure">{amtParts.figure}</span>
              </>
            ) : (
              amtParts.full
            )}
          </span>
          <span className="patient-chats-hub__row-preview-caption">
            {caption}
            {expired ? (
              <span className="patient-chats-hub__row-preview-expiredHint"> · Offer window ended</span>
            ) : null}
          </span>
        </span>
      </span>
    </span>
  );
}

export default function PatientChatsHub() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);
  const [selectedConversationId, setSelectedConversationId] = useState("");
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [mobileChatOpen, setMobileChatOpen] = useState(false);
  const streamRef = useRef(null);
  const streamContentRef = useRef(null);
  const streamEndRef = useRef(null);
  const itemsRef = useRef([]);
  const [bidMeta, setBidMeta] = useState(null);
  const [bidActionError, setBidActionError] = useState("");
  /** From GET …/messages (buildBidHistoryPayload) — overrides list row until next full refresh. */
  const [threadMeta, setThreadMeta] = useState({ name: "", image: "" });
  const [previewAvatarFailed, setPreviewAvatarFailed] = useState(false);
  const [peerOnline, setPeerOnline] = useState(false);
  const [peerLastSeenAt, setPeerLastSeenAt] = useState(null);
  const [rowAvatarFailedIds, setRowAvatarFailedIds] = useState(() => new Set());
  const [narrowChatLayout, setNarrowChatLayout] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(max-width: 1023px)").matches : false
  );
  const { setBottomNavHidden, setAppBarHidden } = usePatientShellChrome();
  /** Bumps when a thread is marked seen so unread styling refreshes without a full reload. */
  const [, setInboxSeenEpoch] = useState(0);

  /** Bottom booking sheet / auto-open only when the thread is open on mobile; desktop always shows the preview pane. */
  const bookingUiInFocusedThread = !narrowChatLayout || mobileChatOpen;

  useEffect(() => {
    const bump = () => setInboxSeenEpoch((n) => n + 1);
    window.addEventListener("healbid_patient_chat_read", bump);
    return () => window.removeEventListener("healbid_patient_chat_read", bump);
  }, []);

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1023px)");
    const fn = () => setNarrowChatLayout(mq.matches);
    fn();
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const syncLayoutChrome = () => {
      if (mq.matches) {
        setMobileChatOpen(false);
        setBottomNavHidden(false);
        setAppBarHidden(false);
      } else {
        setBottomNavHidden(mobileChatOpen);
        /* List: show shell app bar. Thread: hide it — preview head + safe-area replace it. */
        setAppBarHidden(mobileChatOpen);
      }
    };
    syncLayoutChrome();
    mq.addEventListener("change", syncLayoutChrome);
    return () => {
      mq.removeEventListener("change", syncLayoutChrome);
      setBottomNavHidden(false);
      setAppBarHidden(false);
    };
  }, [mobileChatOpen, setBottomNavHidden, setAppBarHidden]);

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    setError("");
    try {
      const res = await apiRequest({
        method: "GET",
        url: ENDPOINTS.GET_CONVERSATIONS,
        params: { limit: 50 },
      });
      const list = Array.isArray(res?.items) ? res.items : [];
      if (!quiet) setRowAvatarFailedIds(new Set());
      setItems(list);
      setSelectedConversationId((prev) => {
        if (prev && list.some((c) => String(c?._id || c?.id) === prev)) return prev;
        const first = list[0]?._id || list[0]?.id;
        return first ? String(first) : "";
      });
    } catch (e) {
      if (!quiet) {
        setError(e?.message || "Could not load conversations");
        setItems([]);
        setSelectedConversationId("");
      }
    } finally {
      if (!quiet) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /** Inbox list: live via personal-room sockets. REST-poll only while disconnected. */
  useEffect(() => {
    let pollId = null;
    const startPolling = () => {
      if (pollId) return;
      pollId = window.setInterval(() => {
        void load({ quiet: true });
      }, 12000);
    };
    const stopPolling = () => {
      if (!pollId) return;
      window.clearInterval(pollId);
      pollId = null;
    };

    const socket = getSocket({ role: "patient" });
    if (!socket) {
      startPolling();
      return () => stopPolling();
    }
    let t = null;
    const schedule = () => {
      window.clearTimeout(t);
      t = window.setTimeout(() => {
        void load({ quiet: true });
      }, 350);
    };
    const onMessageNew = () => schedule();
    const onBid = () => schedule();
    const onNotify = (payload) => {
      const type = String(payload?.type || "").toLowerCase();
      if (type === "chat_message" || type === "chat_reply" || payload?.conversationId) {
        schedule();
      }
    };
    socket.on("message:new", onMessageNew);
    socket.on("bid:update", onBid);
    socket.on("notification:new", onNotify);
    socket.on("connect", stopPolling);
    socket.on("disconnect", startPolling);
    socket.on("connect_error", startPolling);
    if (!socket.connected) startPolling();
    return () => {
      window.clearTimeout(t);
      socket.off("message:new", onMessageNew);
      socket.off("bid:update", onBid);
      socket.off("notification:new", onNotify);
      socket.off("connect", stopPolling);
      socket.off("disconnect", startPolling);
      socket.off("connect_error", startPolling);
      stopPolling();
    };
  }, [load]);

  /** Track open thread for toast suppress (notificationForegroundSuppress). */
  useEffect(() => {
    const cid = String(selectedConversationId || "").trim();
    if (!cid) {
      clearActiveChatConversationId();
      return undefined;
    }
    setActiveChatConversationId(cid);
    return () => clearActiveChatConversationId(cid);
  }, [selectedConversationId]);

  useEffect(() => {
    const open = String(searchParams.get("openConversation") || "").trim();
    if (!open) return;
    if (loading) return;
    if (!items.length) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("openConversation");
          return next;
        },
        { replace: true }
      );
      return;
    }
    const id = String(open);
    const exists = items.some((c) => String(c?._id || c?.id) === id);
    if (!exists) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("openConversation");
          return next;
        },
        { replace: true }
      );
      return;
    }
    setSelectedConversationId(id);
    try {
      if (typeof window !== "undefined" && window.matchMedia("(max-width: 1023px)").matches) {
        setMobileChatOpen(true);
      }
    } catch (_) {}
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("openConversation");
        return next;
      },
      { replace: true }
    );
  }, [items, searchParams, setSearchParams, loading]);

  const selected = useMemo(() => {
    if (!selectedConversationId) return null;
    return (
      items.find((c) => String(c?._id || c?.id) === selectedConversationId) || null
    );
  }, [items, selectedConversationId]);

  useEffect(() => {
    setBidActionError("");
  }, [selectedConversationId]);

  useEffect(() => {
    setThreadMeta({ name: "", image: "" });
    setPreviewAvatarFailed(false);
    setPeerOnline(false);
    setPeerLastSeenAt(null);
  }, [selectedConversationId]);

  useEffect(() => {
    setPreviewAvatarFailed(false);
  }, [threadMeta.image]);

  const selectedPreview = selected?.lastMessagePreview || "No messages yet.";
  const selectedName =
    String(threadMeta.name || "").trim() ||
    (selected ? getConversationTitle(selected) : "") ||
    "Care Team";
  const selectedRole = selected?.targetRole === "hospital" ? "Hospital" : "Doctor";
  const selectedProviderImage = absolutePatientMediaUrl(
    String(threadMeta.image || "").trim() ||
      pickProviderImageFromListItem(selected || {}) ||
      ""
  );
  const showPreviewPhoto = Boolean(selectedProviderImage) && !previewAvatarFailed;

  const providerLastSeenLabel = useMemo(
    () => (peerLastSeenAt ? formatProviderLastSeen(peerLastSeenAt) : ""),
    [peerLastSeenAt]
  );

  const loadMessages = useCallback(async (opts = {}) => {
    const quiet = !!opts.quiet;
    if (!selectedConversationId) {
      setMessages([]);
      setBidMeta(null);
      setThreadMeta({ name: "", image: "" });
      setPeerOnline(false);
      setPeerLastSeenAt(null);
      return;
    }
    if (!quiet) {
      setMessagesLoading(true);
      setBidMeta(null);
    }
    try {
      const res = await apiRequest({
        method: "GET",
        url: ENDPOINTS.GET_CONVERSATION_MESSAGES(selectedConversationId),
        params: { page: 1, limit: 100 },
      });
      const rows = Array.isArray(res?.items)
        ? res.items
        : Array.isArray(res?.messages)
        ? res.messages
        : Array.isArray(res?.data)
        ? res.data
        : [];
      const sorted = [...rows].sort(
        (a, b) => new Date(a?.createdAt || 0).getTime() - new Date(b?.createdAt || 0).getTime()
      );
      setMessages(sorted);
      const nm = String(res?.providerName || res?.conversation?.providerName || "").trim();
      const img = absolutePatientMediaUrl(pickProviderImageFromMessagesPayload(res));
      setThreadMeta({ name: nm, image: img });
      if (selectedConversationId && (nm || img)) {
        setItems((prev) =>
          prev.map((c) => {
            const id = String(c?._id || c?.id || "");
            if (id !== String(selectedConversationId)) return c;
            return {
              ...c,
              ...(nm ? { providerName: nm } : {}),
              ...(img ? { providerImage: img } : {}),
            };
          })
        );
      }
      setBidMeta({
        bidHistory: Array.isArray(res?.bidHistory) ? res.bidHistory : [],
        appointment: res?.appointment ?? null,
        openingBidExpired: Boolean(res?.openingBidExpired),
      });

      if (res && typeof res.providerOnline === "boolean") {
        setPeerOnline(!!res.providerOnline);
        if (res.providerOnline) {
          setPeerLastSeenAt(null);
        } else {
          setPeerLastSeenAt(res.providerLastSeenAt ? String(res.providerLastSeenAt) : null);
        }
      }

      const cid = String(selectedConversationId).trim();
      if (cid) {
        const row = itemsRef.current.find((c) => String(c?._id || c?.id) === cid);
        const convTs = row?.lastMessageAt ? new Date(row.lastMessageAt).getTime() : 0;
        const newestMsgTs = sorted.reduce((max, m) => {
          const t = new Date(m?.createdAt || m?.updatedAt || 0).getTime();
          return Number.isNaN(t) ? max : Math.max(max, t);
        }, 0);
        const markMs = Math.max(
          Number.isFinite(convTs) ? convTs : 0,
          Number.isFinite(newestMsgTs) ? newestMsgTs : 0,
          Date.now()
        );
        markPatientChatThreadSeen(cid, markMs);
      }
    } catch {
      if (!quiet) {
        setMessages([]);
        setBidMeta(null);
        setPeerOnline(false);
        setPeerLastSeenAt(null);
      }
    } finally {
      if (!quiet) setMessagesLoading(false);
    }
  }, [selectedConversationId]);

  useEffect(() => {
    void loadMessages();
  }, [loadMessages]);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState !== "visible" || !selectedConversationId) return;
      void loadMessages({ quiet: true });
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [selectedConversationId, loadMessages]);

  useEffect(() => {
    if (!selectedConversationId) return;
    const socket = getSocket({ role: "patient" });
    const cid = String(selectedConversationId).trim();

    let pollId = null;
    const refreshFromRest = () => {
      void load({ quiet: true });
      void loadMessages({ quiet: true });
    };
    const startPolling = () => {
      if (pollId) return;
      pollId = window.setInterval(refreshFromRest, 12000);
    };
    const stopPolling = () => {
      if (!pollId) return;
      window.clearInterval(pollId);
      pollId = null;
    };

    if (!socket) {
      startPolling();
      return () => stopPolling();
    }
    try {
      socket.emit("conversation:join", { conversationId: cid });
    } catch (_) {}

    const onMessageNew = (payload) => {
      const convId = String(payload?.conversationId || "").trim();
      if (convId !== cid) return;
      const raw = payload?.data != null ? payload.data : payload;
      if (raw && raw._id) {
        setMessages((prev) => {
          if (prev.some((m) => String(m._id) === String(raw._id))) return prev;
          const next = [...prev, raw].sort(
            (a, b) => new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime()
          );
          return next;
        });
        void load({ quiet: true });
        return;
      }
      void Promise.all([load({ quiet: true }), loadMessages()]);
    };

    const onBid = (payload) => {
      const convId = String(payload?.conversationId || "").trim();
      if (convId && convId !== cid) return;
      void loadMessages({ quiet: true });
      void load({ quiet: true });
    };

    const onSlots = (payload) => {
      if (String(payload?.conversationId || "").trim() !== cid) return;
      // quiet: keep bid panel mounted so local booking state (e.g. held slot / pick) is not wiped
      void loadMessages({ quiet: true });
    };

    const onPresence = (payload) => {
      const convId = String(payload?.conversationId || "").trim();
      if (convId !== cid) return;
      const ur = String(payload?.user?.role || "").toLowerCase();
      if (ur !== "doctor" && ur !== "hospital") return;
      if (payload.state === "online") {
        setPeerOnline(true);
        setPeerLastSeenAt(null);
      } else if (payload.state === "offline") {
        setPeerOnline(false);
        if (payload.lastSeenAt) setPeerLastSeenAt(String(payload.lastSeenAt));
      }
    };

    const onPresenceSync = (payload) => {
      const convId = String(payload?.conversationId || "").trim();
      if (convId !== cid) return;
      if (typeof payload.providerOnline !== "boolean") return;
      setPeerOnline(!!payload.providerOnline);
      if (payload.providerOnline) {
        setPeerLastSeenAt(null);
      } else {
        setPeerLastSeenAt(payload.providerLastSeenAt ? String(payload.providerLastSeenAt) : null);
      }
    };

    const rejoinConversation = () => {
      stopPolling();
      try {
        socket.emit("conversation:join", { conversationId: cid });
      } catch (_) {}
    };

    socket.on("message:new", onMessageNew);
    socket.on("bid:update", onBid);
    socket.on("booking_slots:updated", onSlots);
    socket.on("presence:peer", onPresence);
    socket.on("conversation:presenceSync", onPresenceSync);
    socket.on("connect", rejoinConversation);
    socket.on("disconnect", startPolling);
    socket.on("connect_error", startPolling);
    rejoinConversation();
    if (!socket.connected) startPolling();

    return () => {
      try {
        socket.emit("conversation:leave", { conversationId: cid });
      } catch (_) {}
      socket.off("message:new", onMessageNew);
      socket.off("bid:update", onBid);
      socket.off("booking_slots:updated", onSlots);
      socket.off("presence:peer", onPresence);
      socket.off("conversation:presenceSync", onPresenceSync);
      socket.off("connect", rejoinConversation);
      socket.off("disconnect", startPolling);
      socket.off("connect_error", startPolling);
      stopPolling();
    };
  }, [selectedConversationId, load, loadMessages]);

  const hasBidTimeline = useMemo(
    () =>
      Array.isArray(messages) &&
      messages.some(
        (m) =>
          String(m?.messageType || "") === "system" &&
          PATIENT_CHAT_TIMELINE_TYPES.has(String(m?.payload?.type || ""))
      ),
    [messages]
  );

  const timelineProcedureText = useMemo(
    () => procedureLabelFromBidHistory(bidMeta?.bidHistory),
    [bidMeta?.bidHistory]
  );

  const renderedMessages = useMemo(() => {
    if (!Array.isArray(messages) || !messages.length) return [];
    const visible = messages.filter(patientSeesAdminTextMessage);
    return visible.map((m, idx) => {
      const when = formatWhen(m?.createdAt || m?.updatedAt);
      if (String(m?.messageType || "") === "system" && m?.payload?.type) {
        return {
          kind: "system",
          id: String(m?._id || m?.id || `${idx}`),
          message: m,
          when,
        };
      }
      const senderRole = String(m?.senderRole || m?.authorRole || "").toLowerCase();
      if (senderRole === "admin" && String(m?.messageType || "") === "text") {
        return {
          kind: "admin",
          id: String(m?._id || m?.id || `${idx}`),
          message: m,
          when,
        };
      }
      const text = String(m?.text || m?.body || m?.message || "").trim();
      const mine =
        senderRole === "patient" ||
        senderRole === "user" ||
        String(m?.senderType || "").toLowerCase() === "patient" ||
        Boolean(m?.isMine);
      return {
        kind: "text",
        id: String(m?._id || m?.id || `${idx}`),
        text: text || "Message",
        mine,
        when,
        receiptStatus: mine ? getOutgoingReceiptStatus(m) : null,
      };
    });
  }, [messages]);

  /** Chat-style: anchor to newest messages (scrollIntoView + scrollTop and retries after layout/fonts). */
  useLayoutEffect(() => {
    const scrollEl = streamRef.current;
    if (!scrollEl || messagesLoading) return;

    const scrollToBottom = () => {
      scrollEl.scrollTop = scrollEl.scrollHeight;
      try {
        streamEndRef.current?.scrollIntoView({ block: "end", inline: "nearest", behavior: "auto" });
      } catch (_) {
        streamEndRef.current?.scrollIntoView(false);
      }
    };

    scrollToBottom();
    let raf2 = null;
    const raf1 = requestAnimationFrame(() => {
      scrollToBottom();
      raf2 = requestAnimationFrame(scrollToBottom);
    });

    const inner = streamContentRef.current;
    let roInner;
    if (inner && typeof ResizeObserver !== "undefined") {
      roInner = new ResizeObserver(() => scrollToBottom());
      roInner.observe(inner);
    }

    let roPort;
    if (typeof ResizeObserver !== "undefined") {
      roPort = new ResizeObserver(() => scrollToBottom());
      roPort.observe(scrollEl);
    }

    return () => {
      cancelAnimationFrame(raf1);
      if (raf2 != null) cancelAnimationFrame(raf2);
      roInner?.disconnect();
      roPort?.disconnect();
    };
  }, [renderedMessages, mobileChatOpen, messagesLoading, selectedConversationId, hasBidTimeline]);

  useEffect(() => {
    const scrollEl = streamRef.current;
    if (!scrollEl || messagesLoading) return;
    const scrollToBottom = () => {
      scrollEl.scrollTop = scrollEl.scrollHeight;
      try {
        streamEndRef.current?.scrollIntoView({ block: "end", inline: "nearest", behavior: "auto" });
      } catch (_) {
        streamEndRef.current?.scrollIntoView(false);
      }
    };
    const ids = [0, 48, 160, 400].map((ms) => window.setTimeout(scrollToBottom, ms));
    return () => ids.forEach((id) => window.clearTimeout(id));
  }, [renderedMessages, mobileChatOpen, messagesLoading, selectedConversationId, hasBidTimeline]);

  return (
    <PatientAppLayout
      activeNav="messages"
      toolbarTitle={narrowChatLayout && !mobileChatOpen ? "Messages" : undefined}
    >
    <div
      className={`patient-chats-hub${mobileChatOpen ? " patient-chats-hub--mobile-thread" : ""}`}
    >
      <div className="patient-chats-hub__inner">
        {error ? (
          <div className="patient-chats-hub__banner patient-chats-hub__banner--err" role="alert">
            {error}
          </div>
        ) : null}

        {loading ? (
          <PatientChatsHubBoardSkeleton />
        ) : items.length === 0 ? (
          <div className="patient-chats-hub__empty-card">
            <p>No chat record.</p>
          </div>
        ) : (
          <div
            className={`patient-chats-hub__board${mobileChatOpen ? " patient-chats-hub__board--mobile-chat" : ""}`}
          >
            <div className="patient-chats-hub__panel patient-chats-hub__panel--list">
              <ul className="patient-chats-hub__list">
                {items.map((c) => {
                  const id = c?._id || c?.id;
                  const preview = c?.lastMessagePreview || "Tap to open";
                  const when = formatWhen(c?.lastMessageAt || c?.updatedAt);
                  const active = String(id) === selectedConversationId;
                  const unread = isPatientChatThreadUnread(c);
                  const inboxPv = parsePatientChatsInboxPreview(preview, c?.targetRole);
                  const title = getConversationTitle(c);
                  const rowPhoto = absolutePatientMediaUrl(pickProviderImageFromListItem(c));
                  const rowShowPhoto = rowPhoto && !rowAvatarFailedIds.has(String(id));
                  const threadExpired = Boolean(c?.openingBidExpired);
                  return (
                    <li key={String(id)}>
                      <button
                        type="button"
                        className={`patient-chats-hub__row${active ? " is-active" : ""}${
                          unread ? " patient-chats-hub__row--unread" : ""
                        }${threadExpired ? " patient-chats-hub__row--bid-expired" : ""}`}
                        onClick={() => {
                          markPatientChatThreadSeen(String(id), c?.lastMessageAt || Date.now());
                          setInboxSeenEpoch((n) => n + 1);
                          setSelectedConversationId(String(id));
                          if (typeof window !== "undefined" && window.matchMedia("(max-width: 1023px)").matches) {
                            setMobileChatOpen(true);
                          }
                        }}
                      >
                        <span
                          className={`patient-chats-hub__row-avatar${rowShowPhoto ? " patient-chats-hub__row-avatar--photo" : ""}`}
                          aria-hidden
                        >
                          {rowShowPhoto ? (
                            <img
                              src={rowPhoto}
                              alt=""
                              referrerPolicy={avatarImgReferrerPolicy(rowPhoto)}
                              loading="lazy"
                              decoding="async"
                              onError={() => {
                                setRowAvatarFailedIds((prev) => {
                                  const next = new Set(prev);
                                  next.add(String(id));
                                  return next;
                                });
                              }}
                            />
                          ) : (
                            getInitials(title)
                          )}
                        </span>
                        <div className="patient-chats-hub__row-main">
                          <div className="patient-chats-hub__row-topline">
                            <span className="patient-chats-hub__row-lead">
                              <span className="patient-chats-hub__row-title">{title}</span>
                              <span
                                className={`patient-chats-hub__row-role${
                                  c?.targetRole === "hospital"
                                    ? " patient-chats-hub__row-role--hospital"
                                    : " patient-chats-hub__row-role--doctor"
                                }`}
                              >
                                {c?.targetRole === "hospital" ? "Hospital" : "Doctor"}
                              </span>
                            </span>
                            {when ? <span className="patient-chats-hub__row-time">{when}</span> : null}
                          </div>
                          <div className="patient-chats-hub__row-previewline">
                            {inboxPv.variant === "confirmed" ? (
                              <span className="patient-chats-hub__row-preview-confirmed">
                                <CheckCircle2
                                  className="patient-chats-hub__row-preview-confirmed-icon"
                                  size={16}
                                  strokeWidth={2.25}
                                  aria-hidden
                                />
                                <span className="patient-chats-hub__row-preview-confirmed-text">
                                  {inboxPv.confirmedLabel || "Appointment confirmed"}
                                </span>
                              </span>
                            ) : inboxPv.variant === "amount" ? (
                              <PatientChatsHubInboxAmountPreview
                                amount={inboxPv.amount}
                                caption={inboxPv.caption}
                                expired={threadExpired}
                              />
                            ) : inboxPv.variant === "status" ? (
                              <span className="patient-chats-hub__row-preview-status">{inboxPv.label}</span>
                            ) : (
                              <span className="patient-chats-hub__row-preview">{inboxPv.text}</span>
                            )}
                          </div>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="patient-chats-hub__panel patient-chats-hub__panel--preview">
              <div className="patient-chats-hub__preview-head">
                {mobileChatOpen ? (
                  <button
                    type="button"
                    className="patient-chats-hub__back"
                    onClick={() => setMobileChatOpen(false)}
                    aria-label="Back to chat list"
                  >
                    <ChevronLeft className="patient-chats-hub__back-icon" size={22} strokeWidth={2.25} aria-hidden />
                  </button>
                ) : null}
                <div className="patient-chats-hub__preview-head-text">
                  <span
                    className={`patient-chats-hub__preview-avatar${
                      showPreviewPhoto ? " patient-chats-hub__preview-avatar--photo" : ""
                    }`}
                    aria-hidden
                  >
                    {showPreviewPhoto ? (
                      <img
                        src={selectedProviderImage}
                        alt=""
                        referrerPolicy={avatarImgReferrerPolicy(selectedProviderImage)}
                        loading="lazy"
                        decoding="async"
                        onError={() => setPreviewAvatarFailed(true)}
                      />
                    ) : (
                      getInitials(selectedName)
                    )}
                  </span>
                  <div>
                    <p className="patient-chats-hub__preview-name">{selectedName}</p>
                    <p className="patient-chats-hub__preview-role">
                      <span className="patient-chats-hub__preview-role-label">{selectedRole}</span>
                      {peerOnline ? (
                        <>
                          <span className="patient-chats-hub__preview-sep" aria-hidden>
                            {" "}
                            ·{" "}
                          </span>
                          <span className="patient-chats-hub__preview-online">
                            <span className="patient-chats-hub__preview-online-dot" aria-hidden />
                            Online
                          </span>
                        </>
                      ) : peerLastSeenAt ? (
                        <>
                          <span className="patient-chats-hub__preview-sep" aria-hidden>
                            {" "}
                            ·{" "}
                          </span>
                          <span className="patient-chats-hub__preview-lastseen" title={peerLastSeenAt}>
                            {providerLastSeenLabel}
                          </span>
                        </>
                      ) : null}
                    </p>
                  </div>
                </div>
              </div>

              <div
                ref={streamRef}
                className={`patient-chats-hub__preview-stream${
                  !messagesLoading && renderedMessages.length > 0 && hasBidTimeline
                    ? " patient-chats-hub__preview-stream--bid-thread"
                    : ""
                }${messagesLoading ? " patient-chats-hub__preview-stream--loading" : ""}`.trim()}
                aria-busy={messagesLoading}
                aria-label={messagesLoading ? "Loading messages" : undefined}
              >
                {messagesLoading ? (
                  <PatientChatsHubStreamSkeleton />
                ) : renderedMessages.length === 0 ? (
                  <div ref={streamContentRef} className="patient-chats-hub__stream-root">
                    <div className="patient-chats-hub__stream-col">
                      <div className="patient-chats-hub__bubble patient-chats-hub__bubble--in">
                        <div className="patient-chats-hub__bubble-body">
                          <span className="patient-chats-hub__bubble-text">{selectedPreview}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div ref={streamContentRef} className="patient-chats-hub__stream-root">
                    {hasBidTimeline ? (
                      <div className="patient-chats-hub__timeline-intro">
                        <div className="patient-chats-hub__timeline-intro-badge" aria-hidden>
                          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                              d="M7 7h10v10H7z"
                              stroke="currentColor"
                              strokeWidth="1.6"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M9 3v4M15 3v4M7 9H5M19 9h-2"
                              stroke="currentColor"
                              strokeWidth="1.6"
                              strokeLinecap="round"
                            />
                            <path d="M10 12h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                          </svg>
                        </div>
                        <div>
                          <p className="patient-chats-hub__timeline-intro-title">Procedure</p>
                          <p className="patient-chats-hub__timeline-intro-text">
                            {timelineProcedureText ||
                              "Your category and procedure for this visit will appear here once the request is linked."}
                          </p>
                        </div>
                      </div>
                    ) : null}
                    <div
                      className={`patient-chats-hub__stream-col${hasBidTimeline ? " patient-chats-hub__stream-col--ledger" : ""}`.trim()}
                    >
                      {renderedMessages.map((row) =>
                        row.kind === "system" ? (
                          <div
                            key={row.id}
                            className={`patient-chats-hub__sys-wrap ${bidLedgerRowWrapClass(row.message)}`.trim()}
                          >
                            <PatientChatSystemCard
                              message={row.message}
                              when={row.when}
                              providerLabel={selectedRole}
                              openingBidExpired={Boolean(bidMeta?.openingBidExpired)}
                            />
                          </div>
                        ) : row.kind === "admin" ? (
                          <div
                            key={row.id}
                            className="patient-chats-hub__sys-wrap patient-chats-hub__sys-wrap--ledger-neutral"
                          >
                            <PatientChatAdminNoticeCard message={row.message} when={row.when} />
                          </div>
                        ) : (
                          <div
                            key={row.id}
                            className={`patient-chats-hub__bubble ${row.mine ? "patient-chats-hub__bubble--out" : "patient-chats-hub__bubble--in"}`}
                          >
                            <div className="patient-chats-hub__bubble-body">
                              <span className="patient-chats-hub__bubble-text">{row.text}</span>
                              {(row.when || row.receiptStatus) && row.mine ? (
                                <span className="patient-chats-hub__bubble-meta">
                                  {row.when ? <span className="patient-chats-hub__bubble-time">{row.when}</span> : null}
                                  {row.receiptStatus ? (
                                    <>
                                      <span className="patient-chats-hub__bubble-state">
                                        {outgoingReceiptLabel(row.receiptStatus, selectedRole)}
                                      </span>
                                      <span className="patient-chats-hub__bubble-ticks">
                                        <ChatDeliveryTicks status={row.receiptStatus} theme="mint" />
                                      </span>
                                    </>
                                  ) : null}
                                </span>
                              ) : row.when ? (
                                <span className="patient-chats-hub__bubble-time">{row.when}</span>
                              ) : null}
                            </div>
                          </div>
                        )
                      )}
                    </div>
                    <div ref={streamEndRef} className="patient-chats-hub__stream-end" aria-hidden />
                  </div>
                )}
              </div>

              {bidMeta && selectedConversationId ? (
                <PatientChatsHubBidBookingPanel
                  key={selectedConversationId}
                  bidHistory={bidMeta.bidHistory}
                  appointment={bidMeta.appointment}
                  openingBidExpired={Boolean(bidMeta.openingBidExpired)}
                  providerLabel={selectedRole}
                  onRefresh={async () => {
                    await Promise.all([load(), loadMessages({ quiet: true })]);
                  }}
                  inlineError={bidActionError}
                  setInlineError={setBidActionError}
                  bookingUiInFocusedThread={bookingUiInFocusedThread}
                  onRequestOpenMobileThread={() => setMobileChatOpen(true)}
                />
              ) : null}

            </div>
          </div>
        )}
      </div>
    </div>
    </PatientAppLayout>
  );
}
